/**
 * Link Preview Tool - Liquid Glass Preview Modal
 * Fetches and displays post metadata on hover
 */

(function () {
    'use strict';

    // Configuration
    const DWELL_TIME = 400; // ms to wait before fetching
    const MODAL_WIDTH = 400;
    const MODAL_OFFSET = 20; // px from cursor
    const CACHE = new Map();
    const BLACKLIST = [
        '/archive/', '/tag/', '/author/', '/william/', '/work-with-me/',
        '/privacy/', '/terms/', '/digest/', '/portal/'
    ];

    let timer = null;
    let modal = null;
    let currentLink = null;

    // List of content area selectors where we WANT previews to trigger
    const CONTENT_SELECTORS = [
        '.gh-content', '.zine-post-content', '.cwc-content-body',
        '.post-full-content', '.kg-canvas', '.article-content',
        '.zine-page-content'
    ];

    // Detect if valid internal post link within a content area
    function isInternalPostLink(link) {
        if (!link || !link.href) return false;

        // Ensure it's inside a main content area (not a card, header, or grid)
        const isInsideContent = link.closest(CONTENT_SELECTORS.join(', '));
        if (!isInsideContent) return false;

        // Ensure it's NOT inside a link card or CTA area
        if (link.closest('.kg-bookmark-card, .kg-card, .read-more-wrap')) return false;

        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin) return false;

        const path = url.pathname;
        if (path === '/' || path === '' || path.includes('#')) return false;

        // Check blacklist
        if (BLACKLIST.some(b => path.includes(b))) return false;

        // Exclude files
        if (path.match(/\.(png|jpg|pdf|zip|mp4)$/)) return false;

        return true;
    }

    // Initialize UI
    function initUI() {
        if (modal) return;

        modal = document.createElement('div');
        modal.className = 'link-preview-modal';
        document.body.appendChild(modal);
    }

    // Fetch and parse metadata
    async function fetchMetadata(url) {
        if (CACHE.has(url)) return CACHE.get(url);

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Fetch failed');

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Try to find reading time in the DOM first (most reliable)
            const readingTimeSelectors = [
                '.zine-reading-time',
                '.post-card-meta-length',
                '.gh-article-meta-length',
                '.f-reading-time',
                'span.reading-time'
            ];

            let readingTime = null;
            for (const selector of readingTimeSelectors) {
                const el = doc.querySelector(selector);
                if (el && el.innerText.trim()) {
                    readingTime = el.innerText.trim();
                    break;
                }
            }

            // Fallback: Calculate from word count
            if (!readingTime) {
                const contentText = doc.querySelector('.gh-content, .zine-post-content, .cwc-content-body')?.innerText || "";
                if (contentText) {
                    const wordCount = contentText.split(/\s+/).length;
                    const minutes = Math.max(1, Math.ceil(wordCount / 200));
                    readingTime = `${minutes} min read`;
                }
            }

            const data = {
                title: doc.querySelector('meta[property="og:title"]')?.content || doc.title,
                image: doc.querySelector('meta[property="og:image"]')?.content,
                description: doc.querySelector('meta[property="og:description"]')?.content,
                readingTime: readingTime || doc.querySelector('meta[name="twitter:data1"]')?.content,
                url: url
            };

            // Clean up title (remove site name if present)
            if (data.title) data.title = data.title.split(' - ')[0].split(' | ')[0];

            CACHE.set(url, data);
            return data;
        } catch (e) {
            console.warn('Link Preview Error:', e);
            return null;
        }
    }

    // Update Modal Content
    function updateModal(data, isLoading = false) {
        if (isLoading) {
            modal.innerHTML = `
                <div class="link-preview-loader">
                    <div class="link-preview-spinner"></div>
                </div>
            `;
            return;
        }

        if (!data) {
            hideModal();
            return;
        }

        modal.innerHTML = `
            ${data.image ? `<div class="link-preview-image" style="background-image: url('${data.image}')"></div>` : ''}
            <div class="link-preview-content">
                <div class="link-preview-title">${data.title}</div>
                <div class="link-preview-excerpt">${data.description || 'No description available.'}</div>
                <div class="link-preview-meta">
                    <div class="link-preview-read-time">${data.readingTime || ''}</div>
                </div>
                <div class="link-preview-url">${data.url.replace(window.location.origin, '')}</div>
            </div>
        `;
    }

    // Position modal relative to cursor/element
    function positionModal(x, y) {
        let posX = x + MODAL_OFFSET;
        let posY = y + MODAL_OFFSET;

        // Boundary checks
        if (posX + MODAL_WIDTH > window.innerWidth - 20) {
            posX = x - MODAL_WIDTH - MODAL_OFFSET;
        }

        const modalHeight = modal.offsetHeight || 300;
        if (posY + modalHeight > window.innerHeight - 20) {
            posY = y - modalHeight - MODAL_OFFSET;
        }

        modal.style.left = `${posX}px`;
        modal.style.top = `${posY}px`;
    }

    function showModal() {
        modal.classList.add('active');
    }

    function hideModal() {
        if (modal) modal.classList.remove('active');
        clearTimeout(timer);
        currentLink = null;
    }

    // Event Handlers
    function handleMouseEnter(e) {
        const link = e.target.closest('a');
        if (!isInternalPostLink(link)) return;

        // Don't restart if already hovering this link
        if (currentLink === link) return;
        currentLink = link;

        const url = link.href;
        const initialX = e.clientX;
        const initialY = e.clientY;

        timer = setTimeout(async () => {
            initUI();
            updateModal(null, true); // Show loader
            positionModal(initialX, initialY);
            showModal();

            const data = await fetchMetadata(url);
            if (currentLink === link) { // Ensure user is still on the same link
                updateModal(data);
                positionModal(initialX, initialY);
            }
        }, DWELL_TIME);
    }

    function handleMouseMove(e) {
        if (modal && modal.classList.contains('active')) {
            // Optional: update position? User might find it distracting. 
            // Better to keep fixed once shown.
        }
    }

    function handleMouseLeave(e) {
        const link = e.target.closest('a');
        if (link === currentLink) {
            hideModal();
        }
    }

    // Set up listeners
    function init() {
        // Only run on desktop
        if (window.innerWidth <= 1024) return;

        document.addEventListener('mouseover', handleMouseEnter);
        document.addEventListener('mouseout', handleMouseLeave);
        window.addEventListener('scroll', hideModal);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

})();
